import type { ComponentType, ReactNode } from 'react';

import { EmbeddedPricingTiersTable } from './embedded-pricing-tiers-table.tsx';
import { EmbeddedQuestion } from './embedded-question.tsx';
import { EmbeddedQuestionsContainer } from './embedded-questions-container.tsx';

interface EmbeddedRendererProps {
	data: unknown;
}

const embeddedContentRenderers: Record<string, ComponentType<EmbeddedRendererProps>> = {
	'pricing-tiers-table': EmbeddedPricingTiersTable,
	'embedded-question': EmbeddedQuestion,
	'embedded-questions-container': EmbeddedQuestionsContainer,
};

const embeddedContentMarker = /<div data-([a-z-]+)="([^"]+)"><\/div>/g;

export interface EmbeddedContentProps {
	html: string;
	renderHtml: (html: string, key: number) => ReactNode;
}

export const EmbeddedContent = (props: EmbeddedContentProps) => {
	const { html, renderHtml } = props;
	const nodes: ReactNode[] = [];
	let lastIndex = 0;

	for (const match of html.matchAll(embeddedContentMarker)) {
		const [marker, kind, encodedProps] = match;
		const Renderer =
			kind && Object.prototype.hasOwnProperty.call(embeddedContentRenderers, kind)
				? embeddedContentRenderers[kind]
				: undefined;
		if (!Renderer || !encodedProps) {
			continue;
		}
		const index = match.index ?? 0;
		if (index > lastIndex) {
			nodes.push(renderHtml(html.slice(lastIndex, index), nodes.length));
		}
		const data = JSON.parse(decodeURIComponent(encodedProps));
		nodes.push(<Renderer key={nodes.length} data={data} />);
		lastIndex = index + marker.length;
	}

	if (lastIndex < html.length) {
		nodes.push(renderHtml(html.slice(lastIndex), nodes.length));
	}

	return <>{nodes}</>;
};

export interface HasEmbeddedContentArgs {
	html: string;
}

export function hasEmbeddedContent(args: HasEmbeddedContentArgs): boolean {
	const { html } = args;
	return Object.keys(embeddedContentRenderers).some((kind) => html.includes(`data-${kind}=`));
}
